import React, { MouseEvent, useCallback, useState } from 'react';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography } from '@mui/material';

import { AdminPropertyContentResponse } from '~/services/AccommodationService';
import { getTimeDifference } from '~/services/TimeService';

interface Props {
  content: AdminPropertyContentResponse['generatedContents'];
}

const getSourceLabel = (
  contentSource: AdminPropertyContentResponse['generatedContents'][0]['content'][0]['sources'][0],
) => {
  if (contentSource.type === 'bedbank_property') {
    return contentSource.type + ' - ' + contentSource.field;
  }
  return contentSource.type + ' - ' + contentSource.typeId + ' - ' + contentSource.field;
};

export default function AccommodationGeneratedContentView({ content }: Props) {
  // Initialize state once using useState
  const [currentFieldViewing, setCurrentFieldViewing] = useState<Record<string, number>>(() => {
    // Create initial state with all fields set to index 0
    return Array.isArray(content)
      ? content.reduce<Record<string, number>>((acc, field) => {
          acc[field.fields] = 0;
          return acc;
        }, {})
      : {};
  });
  const handleBackClick = useCallback(
    (field: AdminPropertyContentResponse['generatedContents'][0]) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setCurrentFieldViewing((prev) => ({
        ...prev,
        [field.fields]: Math.max(0, (prev[field.fields] || 0) - 1),
      }));
    },
    [],
  );

  const handleForwardClick = useCallback(
    (field: AdminPropertyContentResponse['generatedContents'][0]) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setCurrentFieldViewing((prev) => ({
        ...prev,
        [field.fields]: Math.min(field.content.length - 1, (prev[field.fields] || 0) + 1),
      }));
    },
    [],
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {content.map((field, index) => {
        // Make sure we have a value for this field (handles new fields that might appear)
        const currentIndex = currentFieldViewing[field.fields] !== undefined ? currentFieldViewing[field.fields] : 0;

        return (
          <Accordion key={field.fields + '.' + index} defaultExpanded={field.fields !== 'finePrint'} variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  {field.fields}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button onClick={handleBackClick(field)}>
                    <ChevronLeftIcon />
                  </Button>
                  {currentIndex + 1} / {field.content.length}
                  <Button onClick={handleForwardClick(field)}>
                    <ChevronRightIcon />
                  </Button>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Generated {getTimeDifference(new Date(field.content[currentIndex].updatedAt), new Date(), true, true)}{' '}
                  ago
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', height: '400px' }}>
                <Box sx={{ width: '50%', p: 2, overflow: 'auto' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {field.content[currentIndex].value}
                  </Typography>
                </Box>
                <Box sx={{ width: '50%', p: 2, borderLeft: 1, borderColor: 'divider' }}>
                  <Stack sx={{ height: '100%' }}>
                    <Typography variant="h6">Sources</Typography>
                    <Box sx={{ mt: 1, overflow: 'auto', flexGrow: 1 }}>
                      {field.content[currentIndex].sources.map((source, sourceIndex) => (
                        <Accordion key={sourceIndex}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>{getSourceLabel(source)}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {source.value}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
